import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemePref = 'light' | 'dark' | 'system';
type Resolved = 'light' | 'dark';

interface ThemeState {
  pref: ThemePref;
  setPref: (p: ThemePref) => void;
}

const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      pref: 'system',
      setPref: (pref) => set({ pref }),
    }),
    { name: 'admin-theme' },
  ),
);

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(pref: ThemePref): Resolved {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

/** Applies the theme class to <html> and exposes the current preference. */
export function useTheme() {
  const pref = useThemeStore((s) => s.pref);
  const setPref = useThemeStore((s) => s.setPref);
  const resolved = resolve(pref);

  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const r = resolve(useThemeStore.getState().pref);
      root.classList.toggle('dark', r === 'dark');
    };
    apply();
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [pref]);

  return { pref, resolved, setPref };
}
